/**
 * Utility for persisting component-level draft state in sessionStorage.
 * sessionStorage survives page refresh but dies on tab close.
 */

const PREFIX = 'ot_draft_';

/**
 * Build a storage key from parts.
 * @param {...string} parts - Key segments (e.g., callSheetId, employeeId, field)
 */
export const draftKey = (...parts) => PREFIX + parts.filter(Boolean).join('_');

/**
 * Save a value to sessionStorage.
 */
export const saveDraft = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch { /* quota exceeded – silently ignore */ }
};

/**
 * Load a value from sessionStorage, returning defaultValue if not found.
 */
export const loadDraft = (key, defaultValue = null) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) return defaultValue;
    return JSON.parse(raw);
  } catch {
    return defaultValue;
  }
};

/**
 * Remove a single draft key.
 */
export const removeDraft = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch { /* ignore */ }
};

/**
 * Clear all ot_draft_ entries (e.g., on session die / cleanup).
 */
export const clearAllDrafts = () => {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch { /* ignore */ }
};

// ============================================
// Confirmed Decisions Persistence (used by sagas)
// ============================================

const CONFIRMED_DECISIONS_KEY = 'ot_draft_confirmedDecisions';
const NO_CONTACT_HOLD_PREFIX = `${PREFIX}noContactHold_`;

/**
 * Save a confirmed decision to session storage so it survives refresh.
 */
export const persistConfirmedDecision = (signupId, decision, reqId, transactionId, lastTimeStamp) => {
  const existing = loadDraft(CONFIRMED_DECISIONS_KEY, {});
  existing[signupId] = { status: decision, reqId, transactionId, lastTimeStamp, savedAt: new Date().toISOString() };
  saveDraft(CONFIRMED_DECISIONS_KEY, existing);
};

/**
 * Remove a persisted decision once it reaches a terminal state.
 */
export const clearPersistedDecision = (signupId) => {
  const existing = loadDraft(CONFIRMED_DECISIONS_KEY, {});
  if (existing[signupId]) {
    delete existing[signupId];
    saveDraft(CONFIRMED_DECISIONS_KEY, existing);
  }
};

/**
 * Clear all persisted confirmed decisions (e.g., on callsheet exit).
 */
export const clearAllPersistedDecisions = () => {
  saveDraft(CONFIRMED_DECISIONS_KEY, {});
};

const noContactHoldKey = (callSheetId, employeeId, shiftType) => {
  const normalizedShift = String(shiftType || '').toUpperCase().trim();
  return `${NO_CONTACT_HOLD_PREFIX}${String(callSheetId || '').trim()}_${String(employeeId || '').trim()}_${normalizedShift}`;
};

/**
 * Persist a NoContact hold-until timestamp for a specific callsheet/employee/shift.
 */
export const setNoContactHoldUntil = (callSheetId, employeeId, shiftType, untilMs) => {
  if (!callSheetId || !employeeId || !shiftType || !untilMs) return;
  saveDraft(noContactHoldKey(callSheetId, employeeId, shiftType), {
    untilMs: Number(untilMs) || 0,
    updatedAt: Date.now(),
  });
};

/**
 * Load persisted NoContact hold-until timestamp.
 */
export const getNoContactHoldUntil = (callSheetId, employeeId, shiftType) => {
  if (!callSheetId || !employeeId || !shiftType) return 0;
  const payload = loadDraft(noContactHoldKey(callSheetId, employeeId, shiftType), null);
  return Number(payload?.untilMs) || 0;
};

/**
 * Remove persisted NoContact hold for a specific callsheet/employee/shift.
 */
export const clearNoContactHold = (callSheetId, employeeId, shiftType) => {
  if (!callSheetId || !employeeId || !shiftType) return;
  removeDraft(noContactHoldKey(callSheetId, employeeId, shiftType));
};

/**
 * Remove all expired NoContact hold entries.
 */
export const clearExpiredNoContactHolds = (nowMs = Date.now()) => {
  try {
    const toDelete = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(NO_CONTACT_HOLD_PREFIX)) continue;
      const payload = loadDraft(key, null);
      const untilMs = Number(payload?.untilMs) || 0;
      if (!untilMs || untilMs <= nowMs) {
        toDelete.push(key);
      }
    }
    toDelete.forEach((key) => removeDraft(key));
  } catch {
    // ignore sessionStorage failures
  }
};

/**
 * Merge persisted confirmed decisions into freshly normalized signups from API.
 * Only applies if the API returned a signup with a status that hasn't progressed
 * past the confirmed state (not terminal: ASSIGNED, WB_SUCCESS, REFUSED, WB_FAILED).
 */
export const mergePersistedDecisions = (shiftSignups) => {
  const persisted = loadDraft(CONFIRMED_DECISIONS_KEY, {});
  if (!persisted || Object.keys(persisted).length === 0) return shiftSignups;

  const updatedById = { ...shiftSignups.byId };
  let changed = false;

  Object.entries(persisted).forEach(([signupId, decision]) => {
    const signup = updatedById[signupId];
    if (!signup) return;

    const apiStatus = (signup.status || '').toUpperCase();
    const confirmedStatus = (decision.status || '').toUpperCase();

    // Don't override if API already shows a terminal state
    const terminalStatuses = ['ASSIGNED', 'WBSUCCESS', 'WB_SUCCESS', 'REFUSED', 'WB_FAILED', 'WBFAILED'];
    if (terminalStatuses.includes(apiStatus.replace(/[\s_]/g, ''))) return;

    // Apply persisted decision if API status is behind
    if (confirmedStatus === 'ACCEPT' || confirmedStatus === 'REFUSE') {
      updatedById[signupId] = {
        ...signup,
        status: decision.status,
        selectedDecision: decision.status,
        reqId: decision.reqId || signup.reqId || '',
        transactionId: decision.transactionId || signup.transactionId || '',
        lastTimeStamp: decision.lastTimeStamp || signup.lastTimeStamp || '',
        savedAt: decision.savedAt,
      };
      changed = true;
    }
  });

  if (!changed) return shiftSignups;
  return { ...shiftSignups, byId: updatedById };
};
